import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { RubricConfig, rubricConfigSchema } from '../../../schemas/Rubric.schema';
import {
  createRubricConfig as createRubricConfigQuery,
  deleteRubricConfig as deleteRubricConfigQuery,
  getRubricConfig,
  updateRubricConfig as updateRubricConfigQuery,
} from '../../../services/queries/MaggieRubricQueries';
import { getErrorMessage } from '../../../services/queryHelpers';
import { rubricConfigAtom } from '../../../services/state/RubricsStateJ';

export function useRubricConfigActions() {
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();

  const createNewRubricConfig = useAtomCallback(
    useCallback(
      async (get, set, rubricConfig: Pick<RubricConfig, 'displayName' | 'description'>) => {
        const { type, format } = rubricConfigSchema().getDefault();
        const payload = { ...rubricConfig, type, format };
        try {
          const data = await createRubricConfigQuery(payload);
          const rubricConfigs = await getRubricConfig();
          set(rubricConfigAtom, () => rubricConfigs);
          pushSuccessToast({ message: 'Rubric config created successfully' });
          return data;
        } catch (err) {
          console.error(err);
          pushErrorToast({ message: getErrorMessage(err, 'Failed to create rubric config') });
        }
      },
      [pushErrorToast, pushSuccessToast]
    )
  );

  const updateRubricConfig = useAtomCallback(
    useCallback(
      async (get, set, id: number, payload: Partial<RubricConfig>) => {
        try {
          const data = await updateRubricConfigQuery(id, payload);
          const rubricConfigs = await getRubricConfig();
          set(rubricConfigAtom, () => rubricConfigs);
          pushSuccessToast({ message: 'Rubric config updated successfully' });
          return data;
        } catch (err) {
          console.error(err);
          pushErrorToast({ message: getErrorMessage(err, 'Failed to update rubric config') });
        }
      },
      [pushErrorToast, pushSuccessToast]
    )
  );

  const deleteRubricConfig = useAtomCallback(
    useCallback(
      async (get, set, id: number, force = false) => {
        try {
          await deleteRubricConfigQuery(id, { force });
          set(rubricConfigAtom, async (prev) => {
            const curr = await prev;
            return curr.filter((config) => config.id !== id);
          });
          pushSuccessToast({ message: 'Rubric config deleted successfully' });
        } catch (err) {
          console.error(err);
          pushErrorToast({ message: getErrorMessage(err, 'Failed to delete rubric config') });
        }
      },
      [pushErrorToast, pushSuccessToast]
    )
  );

  return { createNewRubricConfig, updateRubricConfig, deleteRubricConfig };
}
